import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import { useMeasuredSize } from "@acto/taskpane/field-sales-2/utils/hooks/useMeasuredSize";
import { collapseTransition } from "@acto/taskpane/field-sales-2/styles/collapseTransition";

const Clip = styled("div")(({ theme }) => ({
  overflow: "hidden",
  transition: `${collapseTransition(theme, "height")}, opacity ${theme.fieldSalesTwo.motion.duration.slow} ${theme.fieldSalesTwo.motion.easing.standard}`,
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
}));

export interface CollapseProps {
  open: boolean;
  /** Id for the clipped region, e.g. the `aria-controls` target of the trigger. */
  id?: string;
  role?: string;
  "aria-labelledby"?: string;
  children: ReactNode;
}

/**
 * Single-axis vertical collapse. Children mount on first open and stay mounted afterwards, so
 * their layout is deferred until first open and never re-runs on later toggles. Animates between
 * zero and the live content height, tracked with a `ResizeObserver` so the open height follows
 * content that reflows. The closed region is `inert`, so it leaves the tab order and the
 * accessibility tree. Respects `prefers-reduced-motion`.
 */
export function Collapse({ open, id, role, "aria-labelledby": ariaLabelledby, children }: CollapseProps) {
  const [hasOpened, setHasOpened] = useState(open);
  const [contentRef, { height: contentHeight }] = useMeasuredSize();
  const clipRef = useRef<HTMLDivElement>(null);

  if (open && !hasOpened) setHasOpened(true);

  useLayoutEffect(() => {
    if (clipRef.current) clipRef.current.inert = !open;
  }, [open]);

  return (
    <Clip
      ref={clipRef}
      id={id}
      role={role}
      aria-labelledby={ariaLabelledby}
      // Gate opacity on the measured height so the fade runs with the reveal, not before it: on
      // first open the height lags a frame behind `open` while the content is being measured.
      style={{ height: open ? contentHeight : 0, opacity: open && contentHeight > 0 ? 1 : 0 }}
    >
      <div ref={contentRef}>{hasOpened ? children : null}</div>
    </Clip>
  );
}
